import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { makeStyles, TextField } from '@material-ui/core';
import { Search, Calendar } from '@carbon/icons-react';
import 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import { CreateOrderFormSection } from './styles';
import CreateOrderCommonInputBox from './CreateOrderCommonInputBox';
import ko from 'date-fns/locale/ko';
import PostCodeDialog from '@components/Dialog/Postcode/PostCodeDialog';
import { CreatePurchaseOrderInput } from '@generated/graphql';
import { CustomizedTypography } from '@components/Customized';
import { clsx, colorSet } from '@components/Provider';

interface CreateOrderDeliveryInfoProps {
  baseAddress?: string;
  isWithdrawn: boolean;
}

const CreateOrderDeliveryInfo: React.FC<CreateOrderDeliveryInfoProps> = ({
  baseAddress,
  isWithdrawn
}) => {
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    new Date().toISOString()
  );
  const [openPostcodeDialog, setOpenPostcodeDialog] = useState<boolean>(false);
  const [address, setAddress] = useState<{ full: string; short: string }>({
    full: baseAddress ?? '',
    short: ''
  });
  const {
    addressInput,
    datePicker,
    fieldError
  } = useCreateOrderDeliveryInfoStyles();

  const { register, errors, clearErrors, watch } = useFormContext<
    CreatePurchaseOrderInput
  >();

  const watchAddress = watch('shipAddress');

  useEffect(() => {
    if (watchAddress) {
      clearErrors('shipAddress');
    }
  }, [watchAddress, clearErrors]);

  return (
    <CreateOrderFormSection>
      <PostCodeDialog
        open={openPostcodeDialog}
        onClose={() => setOpenPostcodeDialog(false)}
        setAddress={setAddress}
      />
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ko}>
        <CustomizedTypography variant="h4" fontWeight="500" gutterBottoms={8}>
          납품 정보
        </CustomizedTypography>
        <input
          disabled={isWithdrawn}
          type="hidden"
          ref={register({ required: '납품일자를 입력해주세요' })}
          name={'shipDate'}
          value={selectedDate}
        />
        <CreateOrderCommonInputBox
          style={{ marginBottom: 8 }}
          require
          label="납품 일자"
          input={
            <DatePicker
              disabled={isWithdrawn}
              className={datePicker}
              inputVariant="outlined"
              fullWidth
              InputProps={{
                endAdornment: <Calendar color={colorSet.gray700} />
              }}
              disableToolbar
              error={Boolean(errors.shipDate)}
              helperText={errors?.shipDate?.message}
              variant="inline"
              format="yyyy년 MM월 dd일"
              margin="none"
              minDate={new Date()}
              value={selectedDate}
              onChange={(date) => {
                if (date) {
                  setSelectedDate(new Date(date).toISOString());
                }
              }}
            />
          }
        />
        <CreateOrderCommonInputBox
          className={clsx(
            addressInput,
            Boolean(errors?.shipAddress) && fieldError
          )}
          require
          label="납품지 주소"
          input={
            <>
              <TextField
                disabled={isWithdrawn}
                variant="outlined"
                placeholder="납품지 주소 검색"
                inputProps={{
                  readOnly: true,
                  ref: register({
                    required: {
                      value: true,
                      message: '납품지 주소를 입력해주세요'
                    }
                  }),
                  name: 'shipAddress'
                }}
                InputProps={{
                  endAdornment: <Search color={colorSet.gray700} />
                }}
                error={Boolean(errors?.shipAddress)}
                helperText={errors?.shipAddress?.message}
                fullWidth
                value={address.full}
                onClick={() => setOpenPostcodeDialog(true)}
              />

              <TextField
                disabled={isWithdrawn}
                variant="outlined"
                placeholder="상세주소(선택)"
                fullWidth
                name="shipDetailAddress"
                inputProps={{
                  ref: register
                }}
              />
            </>
          }
        />
      </MuiPickersUtilsProvider>
    </CreateOrderFormSection>
  );
};

export default CreateOrderDeliveryInfo;

const useCreateOrderDeliveryInfoStyles = makeStyles((theme) => ({
  datePicker: {
    ...theme.typography.body1
  },
  addressInput: {
    marginBottom: '0px !important'
  },
  fieldError: {
    '& .MuiFormHelperText-root': {
      position: 'relative !important',
      bottom: -2
    }
  }
}));
